<template>
  <div class="card card-tag" :class="`card-${color}`">
    <div class="card-header">
      <div class="card-tag" v-if="tag">
        <h4 class="m-0">{{ tag }}</h4>
      </div>
      <h3>{{ title }}</h3>
    </div>
    <div class="heart-bubble" v-if="withHeart">
      <img src="/imgs/svg/heart_bubble.svg" alt="" />
    </div>
    <div class="card-body">
      <slot />
      
      <!-- New pricing table format -->
      <div v-if="pricingTable" class="pricing-table-section">
        <h5 class="text-center mb-4 pricing-table-title">Check our prices</h5>
        <div class="table-responsive">
          <table class="table table-bordered table-hover">
            <thead class="table-primary">
              <tr>
                <th>PACKAGE</th>
                <th>DETAILS</th>
                <th>WEEKS</th>
                <th>MORNING 15H</th>
                <th>AFTERNOON 15H</th>
                <th>CAMPUS</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in pricingTable" :key="index">
                <td><strong>{{ row.package }}</strong></td>
                <td>{{ row.details }}</td>
                <td>{{ row.weeks }}</td>
                <td>{{ row.morning }}</td>
                <td>{{ row.afternoon }}</td>
                <td>{{ row.campus }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Legacy pricing format (keeping for backward compatibility) -->
      <div v-else-if="pricing" class="detailed-pricing-section">
        <div v-for="(campus, index) in pricing" :key="index" class="campus-section mb-4">
          <div class="campus-header">
            <h6 class="campus-label">{{ campus.title }}</h6>
          </div>
          <div v-for="(price, priceIndex) in campus.prices" :key="priceIndex" class="price-group mb-3">
            <div class="price-line">
              <span class="price-value">{{ price }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="generalPricing" class="general-pricing-section">
        <div class="pricing-header">
          <h6 class="campus-label">{{ generalPricing.campuses }}</h6>
        </div>
        <div class="price-display">
          <span class="price-value">{{ generalPricing.price }}</span>
        </div>
      </div>
      
      <div class="button-container">
        <div class="promotional-line text-center">
          <p class="promotional-text fw-semibold text-blue2 mb-0">
            Looking for the best deal? Talk to our consultants and discover exclusive offers!
          </p>
        </div>
        <button class="btn btn-blue-grad-2" @click="openTab(buttonLink)">{{ button }}</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    color: {
      type: String,
      default: "blue2",
    },
    tag: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    button: {
      type: String,
      default: null,
    },
    buttonLink: {
      type: String,
      default: null,
    },
    withHeart: {
      type: Boolean,
      default: false,
    },
    pricing: {
      type: Array,
      default: null,
    },
    generalPricing: {
      type: Object,
      default: null,
    },
    pricingTable: {
      type: Array,
      default: null,
    },
  },
  methods: {
    openTab(route) {
      if (route) {
        this.$router.push({ name: route });
      }
      this.$emit('click');
    }
  }
};
</script>

<style scoped>
.card-body {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Shared minimal and classy design for both pricing types */
.detailed-pricing-section,
.general-pricing-section,
.pricing-table-section {
  border-top: 1px solid #e9ecef;
  padding-top: 1.5rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
  text-align: center;
}

/* Pricing Table Styles */
.pricing-table-section {
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.pricing-table-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--blue2);
}

.pricing-table-section .table {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  border-radius: 8px;
  overflow: hidden;
}

.pricing-table-section .table-primary {
  background-color: var(--blue2);
  color: white;
}

.pricing-table-section th {
  text-align: center;
  font-weight: 600;
  vertical-align: middle;
  padding: 12px 8px;
}

.pricing-table-section td {
  text-align: center;
  vertical-align: middle;
  padding: 12px 8px;
}

.pricing-table-section tr:nth-child(even) {
  background-color: rgba(240, 240, 240, 0.5);
}

.pricing-table-section tr:hover {
  background-color: rgba(200, 230, 255, 0.3);
}

.campus-section {
  margin-bottom: 2rem;
}

.campus-header,
.pricing-header {
  margin-bottom: 1.5rem;
}

.campus-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--blue2);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0;
  opacity: 0.8;
}

.price-group {
  margin-bottom: 1.5rem;
}

.price-line {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  position: relative;
  padding-bottom: 0.5rem;
}

.price-line::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 2px;
  background: linear-gradient(135deg, var(--blue2), var(--blue3));
  border-radius: 1px;
}

.course-type {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--blue2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.7;
  white-space: nowrap;
}

.price-line .price-value {
  font-size: 1.3rem;
  font-weight: 300;
  color: var(--blue2);
  letter-spacing: -0.5px;
}

.price-display {
  position: relative;
  display: inline-block;
}

.general-pricing-section .price-value {
  font-size: 2rem;
  font-weight: 300;
  color: var(--blue2);
  letter-spacing: -0.5px;
  position: relative;
  display: inline-block;
  padding-bottom: 0.5rem;
}

.general-pricing-section .price-value::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 2px;
  background: linear-gradient(135deg, var(--blue2), var(--blue3));
  border-radius: 1px;
}

.button-container {
  margin-top: auto;
  padding-top: 1rem;
}

.promotional-line {
  background: linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%);
  padding: 0.75rem;
  border-radius: 6px;
  border-left: 3px solid var(--blue2);
  margin-bottom: 5px;
}

.promotional-text {
  line-height: 1.4;
  font-size: 0.9rem;
}
</style>
